/**
 * The ONE reader of an api error's RES-ENV-001 machine code, and the `cause`-chain walk it reads through.
 *
 * Zero imports, so a leaf package (feedback-ui, re-auth) reads the one rule from the `./error-code` subpath without
 * taking the error-handling barrel (the feature-flags `/flag-value` precedent). The barrel re-exports both from here.
 *
 * @module @puffin-core/error-handling/errorCode
 */

/**
 * Walk an error and its `cause` chain, nearest first.
 *
 * ## Why the chain is not optional (AAS-I18N-002 §11.6, measured 2026-08-11)
 *
 * A service layer that adds context by re-throwing
 * `new Error(\`Failed to X: \${error.message}\`, { cause: error })` **destroys the RES-ENV-001
 * envelope** — `code`, `status` and `details` are all gone, and the only thing that survives to the
 * UI is an English sentence with an English prefix. `pos-web`'s `timecardService` does this at **19
 * sites**, and it is the direct reason the clock buttons matched prose: by the time the error
 * reached the component, prose was genuinely all that was left.
 *
 * Rewriting 19 throw sites would fix 19 throw sites. Reading the `cause` chain here fixes every
 * wrapper in the ecosystem, including the ones nobody has found yet, and it costs the wrapper
 * nothing — `{ cause }` is the standard JS mechanism for exactly this and the sites already pass it.
 *
 * Depth is bounded and visited nodes are tracked: a `cause` cycle would otherwise hang the render
 * that resolved the message.
 *
 * @param {*} error
 * @param {number} [maxDepth]
 * @yields {*} each error in the chain, starting with `error` itself
 */
export function* eachErrorInChain(error, maxDepth = 8) {
  const seen = new Set();
  let cur = error;
  for (let i = 0; i < maxDepth && cur && typeof cur === 'object'; i += 1) {
    if (seen.has(cur)) return;
    seen.add(cur);
    yield cur;
    cur = cur.cause;
  }
}

/**
 * Read the RES-ENV-001 machine `code` off an api error, in either error shape.
 *
 * ## Why a client must never branch on the server's PROSE (AAS-I18N-002 §11.6)
 *
 * A client that decides which translated sentence to show by substring-matching the server's
 * `message` has two failure modes, and both were live in this repo when this was written:
 *
 *   1. **The matcher silently dies.** `pos-web` tested `err.message?.includes('No active time
 *      punch')` while pos-api sent `'No active punch found to clock out'`. The branch had a **0 %
 *      hit rate**; every user fell through to the generic handler. Nothing failed — not a test, not
 *      a lint rule, not a census — because a dead branch is indistinguishable from an unused one.
 *   2. **English leaks into the Spanish UI.** The fall-through then interpolated the server's
 *      English sentence into a Spanish template, so the reader saw half a translation.
 *
 * A `code` cannot rot silently in either direction: it is a bounded vocabulary both sides declare,
 * and it survives copy edits, because copy is exactly what it is not.
 *
 * @param {*} error - a processed api error, a raw axios error, or a wrapper carrying either as
 *   its `cause` (see {@link eachErrorInChain})
 * @returns {?string} the machine code, or `null` when absent/not a string
 */
export function readErrorCode(error) {
  for (const e of eachErrorInChain(error)) {
    // The server's code is the hoisted `errorCode` (Q-RC-RESPONSE-ARM, 2026-08-28), ahead of the error's own `code`.
    const fromProcessed = e?.errorCode ?? e?.code;
    const fromRaw = e?.response?.data?.error?.code;
    const code = typeof fromProcessed === 'string' ? fromProcessed : fromRaw;
    if (typeof code === 'string' && code.length > 0) return code;
  }
  return null;
}
